import { AwsRum, type AwsRumConfig } from "aws-rum-web";

const RUM_APPLICATION_ID = "217d3f08-04e4-4571-ac34-3497ac8844d3";
const RUM_APPLICATION_VERSION = "1.0.0";
const RUM_APPLICATION_REGION = "ap-southeast-2";
const RUM_RELEASE_ID = __RELEASE_ID__;

const IGNORED_ERROR_PATTERNS = [
    // Android password-manager / autofill injected callback — not our code
    /_AutofillCallbackHandler/,
    // Transient AWS Cognito identity-pool failure (network blip, expired token)
    /Failed to retrieve Cognito identity/,
    // Cross-origin script error stripped by the browser (CORS) — no actionable detail
    /^Script error\.?$/,
    // Generic resource load failure surfaced by Safari — no actionable detail
    /^Load failed$/,
    // Browser extension (e.g. 1Password, Dashlane) messaging noise
    /No Listener: tabs:outgoing\.message\.ready/,
];

function isIgnoredError(event: ErrorEvent | PromiseRejectionEvent): boolean {
    if (event instanceof ErrorEvent) {
        return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(event.message));
    }

    const message = event.reason instanceof Error ? event.reason.message : String(event.reason);
    return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

// These are all public IDs, no need to keep it secret, they will be visible in user browser dev tools
const RUM_CONFIG: AwsRumConfig = {
    sessionSampleRate: 0.01,
    identityPoolId: "ap-southeast-2:2a73425c-a99a-4267-8155-6897291755b4",
    endpoint: "https://dataplane.rum.ap-southeast-2.amazonaws.com",
    telemetries: ["http", ["errors", { ignore: isIgnoredError }], "performance"],
    allowCookies: false,
    enableXRay: false,
    releaseId: RUM_RELEASE_ID,
    signing: true, // If you have a public resource policy and wish to send unsigned requests please set this to false
    disableAutoPageView: true,
};

/** Module-level instance for error reporting (used by RumErrorBoundary). */
let rumInstance: InstanceType<typeof AwsRum> | null = null;

// Initialize eagerly at module load time so errors thrown during the first
// synchronous render are captured before any useEffect could run.
// __ENABLE_RUM__ is injected by webpack DefinePlugin: true in production, false in dev.
if (__ENABLE_RUM__) {
    try {
        rumInstance = new AwsRum(RUM_APPLICATION_ID, RUM_APPLICATION_VERSION, RUM_APPLICATION_REGION, RUM_CONFIG);
    } catch {
        // Ignore errors thrown during CloudWatch RUM web client initialization
    }
}

export function recordErrorToRum(error: Error | ErrorEvent | string): void {
    rumInstance?.recordError?.(error);
}

/**
 * Records a page view to CloudWatch RUM with a custom page ID.
 * Use this to normalize dynamic URL segments (e.g. per-tenant slugs) into a
 * single logical page ID so that equivalent pages across all organizations are
 * aggregated together in the CloudWatch RUM console.
 */
export function recordPageViewToRum(pageId: string): void {
    rumInstance?.recordPageView?.(pageId);
}

interface IReactErrorEvent {
    errorMessage: string;
    errorName: string;
    componentStack: string;
}

/**
 * Records a React render error to CloudWatch RUM.
 * Sends both the standard JS error (for built-in error telemetry) and a
 * custom event containing the React component stack (requires custom events
 * to be enabled on the app monitor in the CloudWatch RUM console).
 */
export function recordReactErrorToRum(error: Error, componentStack: string): void {
    rumInstance?.recordError?.(error);
    rumInstance?.recordEvent?.("react_component_error", {
        errorName: error.name,
        errorMessage: error.message,
        componentStack,
    } satisfies IReactErrorEvent);
}
